import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IGNORE_META_ERROR_MESSAGE, IGNORE_META_MESSAGE } from '@sb/shared/util';
import { ToastService } from '@sb/ui/toast';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

/**
 * Surfaces failed HTTP responses as toast notifications.
 *
 * Prefers `error.error.meta.status_message` from the response body, falling
 * back to status-code-specific copy (403, 429) and a generic message for
 * everything else. Suppressed when the request opts out via the
 * `ignore-meta-message` header or the IGNORE_META_MESSAGE / IGNORE_META_ERROR_MESSAGE
 * HttpContext tokens — callers that surface errors inline use these to avoid
 * duplicate UI. IGNORE_META_ERROR_MESSAGE only suppresses errors, leaving the
 * MetaInterceptor's success toast intact.
 *
 * The 401 → /logout redirect for stale sessions lives in
 * UnauthorizedRedirectInterceptor and runs regardless of suppression.
 */
@Injectable()
export class ErrorToastInterceptor implements HttpInterceptor {
  private toast = inject(ToastService);
  private translate = inject(TranslateService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const requestPath = request.url;
    return next.handle(request).pipe(
      tap({
        error: (error) => {
          const ignoreErrorToast =
            request?.headers.get('ignore-meta-message') === 'true' ||
            request.context.get(IGNORE_META_MESSAGE) ||
            request.context.get(IGNORE_META_ERROR_MESSAGE);

          if (ignoreErrorToast) {
            return;
          }

          if (error instanceof HttpErrorResponse) {
            this.checkError(error, requestPath);
            return;
          }

          this.serverError(error, requestPath);
        },
      }),
    );
  }

  private checkError(err: HttpErrorResponse, requestPath: string): void {
    if (err.error && err.error.meta) {
      const { meta } = err.error;
      if (meta.status === 'error' && meta.status_message) {
        this.toast.openDanger(meta.status_message);
        return;
      }
    }

    this.serverError(err, requestPath);
  }

  private serverError(err: HttpErrorResponse, requestPath: string): void {
    if (err.status === 403) {
      this.toast.openDanger(this.translate.instant('You do not have permission for this request'));
      return;
    }

    if (err.status === 429) {
      this.toast.openDanger(this.translate.instant('Too many requests'));
      return;
    }

    // The login form owns 401/409 messaging on its own endpoint.
    if ((err.status === 401 || err.status === 409) && requestPath === '/api/auth/login') {
      return;
    }

    // Other 401s are sent to /logout by UnauthorizedRedirectInterceptor — don't
    // toast on top of that.
    if (err.status === 401) {
      return;
    }

    this.toast.openDanger(this.translate.instant('An error has occurred'));
  }
}
